import { useMemo } from "react";

import { parseSubmissionCalendar } from "../../lib/leetcode";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const levelFor = (count) => {
  if (!count) return 0;
  if (count >= 8) return 4;
  if (count >= 5) return 3;
  if (count >= 3) return 2;
  return 1;
};

const toKey = (d) => d.toISOString().slice(0, 10);

// A GitHub/LeetCode-style contribution heatmap rendered from the parsed
// submission calendar (a { "YYYY-MM-DD": count } map).
const LeetCodeCalendar = ({ calendarData, weeks = 53 }) => {
  const { columns, monthLabels } = useMemo(() => {
    const map = parseSubmissionCalendar(calendarData);
    const today = new Date();
    const end = new Date(
      Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
    );
    const start = new Date(end);
    start.setUTCDate(end.getUTCDate() - end.getUTCDay() - (weeks - 1) * 7);

    const cols = [];
    const cur = new Date(start);
    for (let w = 0; w < weeks; w += 1) {
      const col = [];
      for (let d = 0; d < 7; d += 1) {
        const key = toKey(cur);
        const count = map[key] || 0;
        col.push({ key, count, level: levelFor(count), future: cur > end });
        cur.setUTCDate(cur.getUTCDate() + 1);
      }
      cols.push(col);
    }

    const labels = cols
      .map((col, i) => {
        const m = new Date(`${col[0].key}T00:00:00Z`).getUTCMonth();
        if (i === 0) return null; // avoid a cramped label in the first column
        const prevM = new Date(`${cols[i - 1][0].key}T00:00:00Z`).getUTCMonth();
        return prevM !== m ? { index: i, label: MONTHS[m] } : null;
      })
      .filter(Boolean);

    return { columns: cols, monthLabels: labels };
  }, [calendarData, weeks]);

  return (
    <div className="lc-calendar">
      <div className="lccal-scroll">
        <div className="lccal-inner" style={{ "--weeks": weeks }}>
          <div className="lccal-months">
            {monthLabels.map((m) => (
              <span
                key={m.index}
                className="lccal-month"
                style={{ gridColumn: m.index + 1 }}
              >
                {m.label}
              </span>
            ))}
          </div>
          <div className="lccal-grid">
            {columns.map((col, ci) =>
              col.map((cell, ri) => (
                <span
                  key={cell.key}
                  className={`lccal-cell lvl-${cell.level}${
                    cell.future ? " is-future" : ""
                  }`}
                  style={{ gridColumn: ci + 1, gridRow: ri + 1 }}
                  title={
                    cell.future
                      ? undefined
                      : `${cell.count} submission${
                          cell.count === 1 ? "" : "s"
                        } on ${cell.key}`
                  }
                />
              ))
            )}
          </div>
        </div>
      </div>
      <div className="lccal-legend">
        <span>Less</span>
        {[0, 1, 2, 3, 4].map((l) => (
          <span key={l} className={`lccal-cell lvl-${l}`} />
        ))}
        <span>More</span>
      </div>
    </div>
  );
};

export default LeetCodeCalendar;
